/**
 * Lo que la IA no pudo hacer.
 *
 * Cada fila es algo que un cliente pidió al crear un sitio o una página y no
 * cupo: una sección que no tenemos, un segundo botón que el template elegido no
 * enseña, una letra que no está en la lista. Agrupado por frase y ordenado por
 * cuántas personas distintas lo pidieron: diez intentos de la misma persona no
 * son diez clientes.
 *
 * Es para decidir qué construir, no para depurar un caso: por eso aquí no hay
 * prompt ni proyecto, sólo la frase (ver `ai_unmet_requests`). El caso entero
 * —prompt, plan, proyecto— está en la otra pestaña, «Generaciones»
 * (`AdminAiGenerations`), que es la que se abre primero.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IconSparkles } from '@tabler/icons-react'
import { VelticaMascot } from '@webbuilder/ui'
import { DashboardPageHeader } from '../DashboardPageHeader'
import { listAiUnmet, type AdminAiUnmet as UnmetRow } from '../../lib/api'
import { AdminAiGenerations } from './AdminAiGenerations'
import { AdminAiSpend } from './AdminAiSpend'
import { activeLanguage, translateServerError, useI18n } from '../../lib/i18n'

type Kind = UnmetRow['kind'] | 'all'

const KINDS: { id: Kind; labelKey: string }[] = [
  { id: 'all', labelKey: 'aiUnmet.kind.all' },
  { id: 'section', labelKey: 'aiUnmet.kind.section' },
  { id: 'element', labelKey: 'aiUnmet.kind.element' },
  { id: 'font', labelKey: 'aiUnmet.kind.font' },
  { id: 'style', labelKey: 'aiUnmet.kind.style' },
  { id: 'feature', labelKey: 'aiUnmet.kind.feature' },
]

const PERIODS = [7, 30, 90] as const

export function AdminAiUnmet({ token }: { token: string }) {
  const { t } = useI18n()
  const [view, setView] = useState<'generations' | 'unmet' | 'spend'>('generations')
  const [days, setDays] = useState<number>(30)
  const [kind, setKind] = useState<Kind>('all')
  const [rows, setRows] = useState<UnmetRow[] | null>(null)
  const [failed, setFailed] = useState('')

  const load = useCallback(async () => {
    setFailed('')
    setRows(null)
    try {
      setRows(await listAiUnmet(token, days))
    } catch (err) {
      setFailed(translateServerError(err, t('aiUnmet.loadFailed')))
      setRows([])
    }
  }, [token, days, t])

  useEffect(() => { if (view === 'unmet') void load() }, [load, view])

  const visible = useMemo(() => (rows ?? []).filter((row) => kind === 'all' || row.kind === kind), [rows, kind])
  /* Lo detectado por el código se guarda como «secondaryAction in hero»: así
     se agrupa igual siempre. Aquí se dice en cristiano. */
  const label = (what: string) => {
    const match = /^(eyebrow|subheadline|primaryAction|secondaryAction|items|image) in (.+)$/.exec(what)
    return match ? t('aiUnmet.fieldIn', { field: t(`aiUnmet.field.${match[1]!}`), category: match[2]! }) : what
  }
  const date = (iso: string) => new Date(iso).toLocaleDateString(activeLanguage(), { day: 'numeric', month: 'short' })

  return (
    <div className="dash-dashboard-view dash-standard-page">
      <DashboardPageHeader title={t('nav.aiUnmet')} subtitle={t(view === 'generations' ? 'aiGen.subtitle' : view === 'spend' ? 'aiSpend.subtitle' : 'aiUnmet.subtitle')} />

      <div className="dash-ai-unmet-toolbar">
        <div className="dash-ai-unmet-segment" role="tablist" aria-label={t('nav.aiUnmet')}>
          {(['generations', 'unmet', 'spend'] as const).map((id) => (
            <button key={id} type="button" role="tab" aria-selected={view === id} className={view === id ? 'is-active' : ''} onClick={() => { setView(id) }}>
              {t(id === 'generations' ? 'aiGen.tab' : id === 'spend' ? 'aiSpend.tab' : 'aiUnmet.tab')}
            </button>
          ))}
        </div>
        <div className="dash-ai-unmet-segment" role="tablist" aria-label={t('aiUnmet.period')}>
          {PERIODS.map((period) => (
            <button key={period} type="button" role="tab" aria-selected={days === period} className={days === period ? 'is-active' : ''} onClick={() => { setDays(period) }}>
              {t('aiUnmet.lastDays', { count: period })}
            </button>
          ))}
        </div>
      </div>

      {view === 'generations' ? <AdminAiGenerations token={token} days={days} /> : view === 'spend' ? <AdminAiSpend token={token} days={days} /> : (<>
      <div className="dash-ai-unmet-toolbar">
        <div className="dash-ai-unmet-segment" role="tablist" aria-label={t('aiUnmet.col.kind')}>
          {KINDS.map((option) => (
            <button key={option.id} type="button" role="tab" aria-selected={kind === option.id} className={kind === option.id ? 'is-active' : ''} onClick={() => { setKind(option.id) }}>
              {t(option.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {failed ? (
        <div className="dash-inline-msg">
          <span>{failed}</span>
          <div className="dash-inline-msg-actions">
            <button type="button" className="dash-secondary-btn" onClick={() => { void load() }}>{t('aiUnmet.retry')}</button>
          </div>
        </div>
      ) : null}

      {rows === null ? (
        <p className="dash-card-description">{t('aiUnmet.loading')}</p>
      ) : visible.length === 0 ? (
        <div className="dash-placeholder">
          <VelticaMascot outline size={36} />
          <strong>{t('aiUnmet.empty')}</strong>
          <span>{t('aiUnmet.emptyBody')}</span>
        </div>
      ) : (
        <div className="dash-ai-unmet-wrap">
          <table className="dash-ai-unmet-table">
            <thead>
              <tr>
                <th>{t('aiUnmet.col.what')}</th>
                <th>{t('aiUnmet.col.kind')}</th>
                <th className="is-number">{t('aiUnmet.col.people')}</th>
                <th className="is-number">{t('aiUnmet.col.times')}</th>
                <th>{t('aiUnmet.col.source')}</th>
                <th>{t('aiUnmet.col.lastSeen')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={`${row.kind}:${row.what}`}>
                  <td>
                    <strong>{label(row.what)}</strong>
                    {/* En una sección que no existe, la que se usó en su lugar. En un
                        elemento la categoría ya va en el nombre. */}
                    {row.kind === 'section' && row.categories.length ? <small>{t('aiUnmet.instead', { categories: row.categories.join(', ') })}</small> : null}
                  </td>
                  <td>{t(`aiUnmet.kind.${row.kind}`)}</td>
                  <td className="is-number">{row.people}</td>
                  <td className="is-number">{row.total}</td>
                  <td>
                    {/* Lo detectado por el código es más fiable que lo que dice la IA. */}
                    {row.fromCode ? <em className="dash-billing-pill is-ok">{t('aiUnmet.fromCode')}</em> : null}
                    {row.fromAi ? <em className="dash-billing-pill is-soft">{t('aiUnmet.fromAi')}</em> : null}
                  </td>
                  <td>{date(row.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  )
}
